import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { TranslateService } from '@ngx-translate/core';
import { convertHHMMToDate, formatTimeToHHMM } from '@shared/helpers/standarFormat';
import { CampaignContextService } from '@shared/services/campaign-context.service';
import { CampaignModel } from '@modules/campaigns/models/campaign.model';
import { RegionalConfigService } from '@admin/modules/regional-configs/services/regional-configs.service';
import { RegionalConfigsModel } from '@admin/modules/regional-configs/model/regional-configs.model';
type Interval = {
  start: Date | null;
  finish: Date | null;
};

@Component({
  selector: 'app-message-wait-nodes',
  templateUrl: './message-wait-nodes.component.html',
  styleUrls: ['./message-wait-nodes.component.scss'],
})
export class MessageWaitNodesComponent implements OnInit, OnChanges {
  @Input() selectedNode?: NodeClass;
  @Input() campaignSelected?: CampaignModel;
  @Output() selectedNodeChange = new EventEmitter<NodeClass>();

  private daysOfWeekTranslations: { [key: string]: string } = {};
  protected readonly NodeType = NodeType;

  timeOfWeekTranslations: { [key: string]: string } = {};
  showRetryRange: boolean = false; // Checkbox model
  interval: Interval | null = {
    start: new Date(),
    finish: new Date(new Date().getTime() + 60 * 60 * 1000),
  };
  rangeErrors: { invalidOrder?: boolean; overlaps?: boolean } = {};
  accordionVisible = false;
  regionalConfig: RegionalConfigsModel = {} as RegionalConfigsModel;
  groupedCollectors: any[] = [];

  constructor(
    private translate: TranslateService,
    private campaignContext: CampaignContextService,
    private regionalConfigService: RegionalConfigService,
  ) {}

  ngOnInit(): void {
    this.loadTranslations();

    this.campaignContext.campaign$.subscribe((campaign) => {
      if (campaign) {
        this.campaignSelected = campaign;
      }
    });

    if (this.selectedNode?.type == NodeType.WaitDays) {
      const rawInterval = this.selectedNode.config['interval'];

      this.interval = rawInterval
        ? {
            start: convertHHMMToDate(rawInterval.start),
            finish: convertHHMMToDate(rawInterval.finish),
          }
        : {
            start: new Date(),
            finish: new Date(new Date().getTime() + 60 * 60 * 1000),
          };

      this.showRetryRange = rawInterval != null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedNode'] && this.selectedNode?.type === NodeType.WaitDays) {
      const rawInterval = this.selectedNode.config?.['interval'];

      this.interval = rawInterval
        ? {
            start: convertHHMMToDate(rawInterval.start),
            finish: convertHHMMToDate(rawInterval.finish),
          }
        : {
            start: new Date(),
            finish: new Date(new Date().getTime() + 60 * 60 * 1000),
          };

      this.validateInterval();
      this.showRetryRange = rawInterval != null;
    }
  }
  private loadTranslations(): void {
    this.translate
      .get('DATA.COMMON.TIME_OF_WEEK')
      .subscribe((translations: { [key: string]: string }) => {
        this.timeOfWeekTranslations = translations;
      });

    this.translate
      .get('DATA.COMMON.DAYS_OF_WEEK')
      .subscribe((translations: { [key: string]: string }) => {
        this.daysOfWeekTranslations = translations;
      });
  }

  translateTime(day: string): string {
    return this.timeOfWeekTranslations[day] || day;
  }

  translateDay(day: string): string {
    return this.daysOfWeekTranslations[day] || day;
  }

  getPartDay(): string {
    return this.showRetryRange && this.interval
      ? `${formatTimeToHHMM(this.interval?.start)} - ${formatTimeToHHMM(this.interval?.finish)}`
      : this.translateTime(this.selectedNode?.config?.['partDay'] || '-');
  }

  getWaitDays(): string {
    return this.selectedNode?.config?.['waitDays'] ?? '0';
  }

  getDayOfWeek(): string {
    return this.translateDay(this.selectedNode?.config?.['dayOfWeek'] || '-');
  }

  getDayOfMonth(): string {
    const dayOfMonth = this.selectedNode?.config?.['dayOfMonth'];
    return dayOfMonth === 'last'
      ? this.translate.instant('DATA.COMMON.LAST_DAY')
      : dayOfMonth || '-';
  }

  onRetryRangeTimeChange(key: keyof Interval, value: Date | null): void {
    if (!this.interval) return; // Early return if null

    this.interval[key] = value;

    this.selectedNode!.config['interval'] = {
      start: formatTimeToHHMM(this.interval.start),
      finish: formatTimeToHHMM(this.interval.finish),
    };

    this.validateInterval();
    this.selectedNodeChange.emit(this.selectedNode!);
  }

  private validateInterval(): void {
    this.rangeErrors = {};

    if (!this.interval?.start || !this.interval?.finish) return;

    const start = this.interval.start.getTime();
    const finish = this.interval.finish.getTime();

    if (start >= finish) {
      this.rangeErrors.invalidOrder = true;
    }
  }

  manageRetryRange() {}

  private groupCollectorsByRegionalConfigs(
    campaignCollectors: string[],
    regionalConfigs: any[],
  ): any[] {
    const result: any[] = [];
    regionalConfigs.forEach((config) => {
      const { timezone, timetable, collectors = [] } = config;

      const matchedCollectors = collectors.filter((c: any) => campaignCollectors.includes(c.id));

      if (matchedCollectors.length) {
        result.push({
          timezone,
          collectors: matchedCollectors,
          timetable: {
            MORNING: timetable?.MORNING || null,
            EVENING: timetable?.EVENING || null,
            FULL_DAY: timetable?.FULL_DAY || null,
          },
        });
      }
    });

    return result;
  }

  getTimetableEntries(timetable: any) {
    if (!timetable) return [];

    const order = ['MORNING', 'EVENING', 'FULL_DAY'];

    return Object.entries(timetable)
      .map(([key, value]: [string, any]) => ({
        key,
        label: this.mapPeriodLabel(key),
        start: value?.start,
        finish: value?.end || value?.finish,
      }))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  }

  private mapPeriodLabel(key: string): string {
    switch (key) {
      case 'MORNING':
        return 'Mañana';
      case 'EVENING':
        return 'Tarde';
      case 'FULL_DAY':
        return 'Todo el día';
      default:
        return key;
    }
  }

  getCollectorNames(group: any): string {
    return group.collectors?.map((c: any) => c.name).join(', ') || '';
  }

  getCollectorNamesShort(group: any): string {
    const full = this.getCollectorNames(group);
    return full.length > 35 ? full.slice(0, 35) + '…' : full;
  }

  toggleAccordion() {
    this.accordionVisible = !this.accordionVisible;

    if (this.accordionVisible && this.groupedCollectors.length === 0 && this.campaignSelected) {
      this.regionalConfigService.listRegionalConfigs().subscribe((regionalConfigs: any) => {
        const regionalConfigsa = regionalConfigs?.data?.regionalConfigs || [];

        this.groupedCollectors = this.groupCollectorsByRegionalConfigs(
          this.campaignSelected!.collectorIds!,
          regionalConfigsa,
        );
      });
    }
  }
}
